import { memo, useCallback, useState } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { NewDialog, RowActions, useColumns } from 'domains/ContentProcessing/AvcInstances'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { avcInstancesOverviewRoute } from 'pages/NWPlatform/AvcSystem/Instances'
import { dashboardRoute } from 'pages/NWPlatform/AvcSystem/Instances/Instance'
import { avcInstanceQueryKeys, avcInstanceTaskQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: avcInstancesOverviewRoute.fullPath })
  const { environmentId } = avcInstancesOverviewRoute.useParams()
  const { query } = avcInstancesOverviewRoute.useSearch()

  const [newDialogOpen, setNewDialogOpen] = useState(false)

  const avcInstancesQuery = useInfiniteQuery({
    ...avcInstanceQueryKeys.list({ environmentId, query }),
    select: (data) => ({
      ...data,
      flat: data.pages.flatMap((page) => page.items),
    }),
  })

  const columns = useColumns()

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:ContentProcessing.Instance.searchAvcInstances')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.invalidateQueries({ queryKey: avcInstanceQueryKeys.list({ environmentId }).queryKey })

            queryClient.invalidateQueries({ queryKey: avcInstanceTaskQueryKeys.lists() })
          }, [environmentId, queryClient])}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        data={avcInstancesQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={avcInstancesQuery.isInitialLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={avcInstancesOverviewRoute.fullPath}
            to={dashboardRoute.to}
            params={{ avcInstanceId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id', '_tasks_running']}
        enableInfinity
        infinityProps={{
          hasNextPage: avcInstancesQuery.hasNextPage,
          isFetchingNextPage: avcInstancesQuery.isFetchingNextPage,
          fetchNextPage: avcInstancesQuery.fetchNextPage,
        }}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('domain:ContentProcessing.Instance.avcInstance'),
        })}
        RowActions={(props) => <RowActions {...props} />}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} disableRestoreFocus>
        <NewDialog />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
